import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { captureAttribution } from "@/lib/utm";
import { redirectRoutes } from "@/components/site/RedirectRoutes";
import { PrivateRoute } from "@/components/site/PrivateRoute";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import Index from "./pages/Index.tsx";
import Strategy from "./pages/Strategy.tsx";
import StrategySession from "./pages/StrategySession.tsx";
import StrategySessionThankYou from "./pages/StrategySessionThankYou.tsx";
import MasterclassThankYou from "./pages/MasterclassThankYou.tsx";
import Scan from "./pages/Scan.tsx";
import Framework from "./pages/Framework.tsx";
import HonestKingdomVoice from "./pages/HonestKingdomVoice.tsx";
import Links from "./pages/Links.tsx";
import AppHome from "./pages/AppHome.tsx";
import About from "./pages/About.tsx";
import Speaking from "./pages/Speaking.tsx";
import Books from "./pages/Books.tsx";
import Ebooks from "./pages/Ebooks.tsx";
import Reviews from "./pages/Reviews.tsx";
import Invest from "./pages/Invest.tsx";
import Advocacy from "./pages/Advocacy.tsx";
import AdvocacyPricing from "./pages/AdvocacyPricing.tsx";
import AdvocacyHowItWorks from "./pages/AdvocacyHowItWorks.tsx";
import AdvocacyIntake from "./pages/AdvocacyIntake.tsx";
import CompassionApply from "./pages/CompassionApply.tsx";
import AdvocacyAgreement from "./pages/AdvocacyAgreement.tsx";
import Free from "./pages/Free.tsx";
import Legal from "./pages/Legal.tsx";
import Funnel from "./pages/Funnel.tsx";
import FunnelTiers from "./pages/FunnelTiers.tsx";
import NotFound from "./pages/NotFound.tsx";
import SearchPage from "./pages/Search.tsx";

import BookSession from "./pages/BookSession.tsx";
import CheckoutReturn from "./pages/CheckoutReturn.tsx";
import Auth from "./pages/Auth.tsx";
import Members from "./pages/Members.tsx";
import ResetPassword from "./pages/ResetPassword.tsx";
import AdminCheckouts from "./pages/AdminCheckouts.tsx";
import AdminAbandonedEmails from "./pages/AdminAbandonedEmails.tsx";
import AdminEmailPreviews from "./pages/AdminEmailPreviews.tsx";
import AdminSubmissions from "./pages/AdminSubmissions.tsx";
import AdminLeads from "./pages/AdminLeads.tsx";
import AdminLeadInsight from "./pages/AdminLeadInsight.tsx";
import AdminAppProgress from "./pages/AdminAppProgress.tsx";
import AdminTestimonials from "./pages/AdminTestimonials.tsx";
import AdminTestimonialsPreview from "./pages/AdminTestimonialsPreview.tsx";
import AdminTestimonialAudit from "./pages/AdminTestimonialAudit.tsx";
import AdminTestimonialReleases from "./pages/AdminTestimonialReleases.tsx";
import MyTestimonial from "./pages/MyTestimonial.tsx";
import AdminNotifications from "./pages/AdminNotifications.tsx";
import AdminEscalations from "./pages/AdminEscalations.tsx";

import AdminCompassion from "./pages/AdminCompassion.tsx";
import AdminIntakes from "./pages/AdminIntakes.tsx";
import OAuthConsent from "./pages/OAuthConsent.tsx";
import Blog from "./pages/Blog.tsx";
import BlogPost from "./pages/BlogPost.tsx";
import PRTemplate from "./pages/PRTemplate.tsx";
import QAHero from "./pages/QAHero.tsx";
import QAHeroFrame from "./pages/QAHeroFrame.tsx";
import QAHeroPerf from "./pages/QAHeroPerf.tsx";
import QAAbout from "./pages/QAAbout.tsx";
import QAAboutFrame from "./pages/QAAboutFrame.tsx";

import Masterclass from "./pages/Masterclass.tsx";
import BuiltIndex from "./pages/BuiltIndex.tsx";
import BuiltIndexStart from "./pages/BuiltIndexStart.tsx";
import BuiltIndexDiagnostic from "./pages/BuiltIndexDiagnostic.tsx";
import BuiltIndexDisc from "./pages/BuiltIndexDisc.tsx";
import BuiltIndexFuelDrain from "./pages/BuiltIndexFuelDrain.tsx";
import BuiltIndexListening from "./pages/BuiltIndexListening.tsx";
import BuiltIndexResults from "./pages/BuiltIndexResults.tsx";
import AdminBuiltIndex from "./pages/AdminBuiltIndex.tsx";
import ShareStory from "./pages/ShareStory.tsx";
import Stories from "./pages/Stories.tsx";
import TestimonialIntake from "./pages/TestimonialIntake.tsx";
import { FloatingMark } from "@/components/site/FloatingMark";
import { LeadMagnetWidget } from "@/components/site/LeadMagnetWidget";
import { StickyCTABar } from "@/components/site/StickyCTABar";
import { ErrorBoundary } from "@/components/site/ErrorBoundary";
import { ScrollToTop } from "@/components/site/ScrollToTop";
import { CTAEventQA } from "@/components/site/CTAEventQA";
import { HeroBudgetWarning } from "@/components/site/HeroBudgetWarning";
import { useAnalytics } from "@/hooks/useAnalytics";
import { useBookClickTracking } from "@/lib/book-click-tracking";

const queryClient = new QueryClient();

const AttributionBridge = () => {
  const location = useLocation();
  useEffect(() => {
    captureAttribution();
  }, [location.pathname, location.search]);
  return null;
};

const AnalyticsBridge = () => {
  useAnalytics();
  // Delegated tracking for every #book CTA (inline, footer, sticky mobile).
  useBookClickTracking();
  return null;
};


const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <AttributionBridge />
        <AnalyticsBridge />
        <ErrorBoundary label="Routes" variant="page">
          <Routes>
            {/* Legacy / moved / renamed URLs — see src/lib/redirect-map.ts */}
            {redirectRoutes}
            <Route path="/" element={<Index />} />
            <Route path="/inquire" element={<Strategy />} />
            <Route path="/strategy-session" element={<StrategySession />} />
            <Route path="/strategy-session/thank-you" element={<StrategySessionThankYou />} />
            <Route path="/framework" element={<Framework />} />
            <Route path="/scan" element={<Scan />} />
            <Route path="/honest-kingdom-voice" element={<HonestKingdomVoice />} />
            <Route path="/links" element={<Links />} />
            <Route path="/app" element={<AppHome />} />
            <Route path="/about" element={<About />} />
            <Route path="/speaking" element={<Speaking />} />
            <Route path="/books" element={<Books />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/ebooks" element={<Ebooks />} />
            <Route path="/reviews" element={<Reviews />} />
            <Route path="/invest" element={<Invest />} />
            <Route path="/masterclass" element={<Masterclass />} />
            <Route path="/masterclass/thank-you" element={<MasterclassThankYou />} />
            <Route path="/built-index" element={<BuiltIndex />} />
            <Route path="/built-index/start" element={<BuiltIndexStart />} />
            <Route path="/built-index/diagnostic" element={<BuiltIndexDiagnostic />} />
            <Route path="/built-index/disc" element={<BuiltIndexDisc />} />
            <Route path="/built-index/fuel-drain" element={<BuiltIndexFuelDrain />} />
            <Route path="/built-index/listening" element={<BuiltIndexListening />} />
            <Route path="/built-index/results" element={<BuiltIndexResults />} />
            <Route path="/stories" element={<Stories />} />
            {/* Target of the WebSite SearchAction — must stay at /search?q= */}
            <Route path="/search" element={<SearchPage />} />

            <Route path="/share-your-story" element={<ShareStory />} />
            <Route path="/testimonial-intake" element={<TestimonialIntake />} />
            <Route path="/advocacy" element={<Advocacy />} />
            <Route path="/advocacy/pricing" element={<AdvocacyPricing />} />
            <Route path="/advocacy/how-it-works" element={<AdvocacyHowItWorks />} />
            <Route path="/advocacy/intake" element={<AdvocacyIntake />} />
            <Route path="/advocacy/compassion-apply" element={<CompassionApply />} />
            <Route path="/advocacy/agreement" element={<AdvocacyAgreement />} />
            <Route path="/free" element={<Free />} />
            <Route path="/pr-template" element={<PRTemplate />} />
            <Route path="/legal" element={<Legal />} />
            <Route path="/funnel" element={<Funnel />} />
            <Route path="/funnel/tiers" element={<FunnelTiers />} />
            <Route path="/book" element={<BookSession />} />
            <Route path="/checkout/return" element={<CheckoutReturn />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/members" element={<Members />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            {/* Internal routes: self-referencing canonical + noindex via PrivateRoute. */}
            <Route path="/admin/checkouts" element={<PrivateRoute path="/admin/checkouts" title="Checkouts — Admin"><AdminCheckouts /></PrivateRoute>} />
            <Route path="/admin/abandoned-emails" element={<PrivateRoute path="/admin/abandoned-emails" title="Abandoned emails — Admin"><AdminAbandonedEmails /></PrivateRoute>} />
            <Route path="/admin/email-previews" element={<PrivateRoute path="/admin/email-previews" title="Email previews — Admin"><AdminEmailPreviews /></PrivateRoute>} />
            <Route path="/admin/submissions" element={<PrivateRoute path="/admin/submissions" title="Submissions — Admin"><AdminSubmissions /></PrivateRoute>} />
            <Route path="/admin/leads" element={<PrivateRoute path="/admin/leads" title="Leads — Admin"><AdminLeads /></PrivateRoute>} />
            <Route path="/admin/lead-insight" element={<PrivateRoute path="/admin/lead-insight" title="Lead Insight — Admin"><AdminLeadInsight /></PrivateRoute>} />
            <Route path="/admin/app-progress" element={<PrivateRoute path="/admin/app-progress" title="App Progress — Admin"><AdminAppProgress /></PrivateRoute>} />
            <Route path="/admin/testimonials" element={<PrivateRoute path="/admin/testimonials" title="Testimonials — Admin"><AdminTestimonials /></PrivateRoute>} />
            <Route path="/admin/testimonials/preview" element={<PrivateRoute path="/admin/testimonials/preview" title="Testimonial preview — Admin"><AdminTestimonialsPreview /></PrivateRoute>} />
            <Route path="/admin/testimonials/audit" element={<PrivateRoute path="/admin/testimonials/audit" title="Testimonial audit — Admin"><AdminTestimonialAudit /></PrivateRoute>} />
            <Route path="/admin/testimonials/releases" element={<PrivateRoute path="/admin/testimonials/releases" title="Testimonial releases — Admin"><AdminTestimonialReleases /></PrivateRoute>} />
            <Route path="/my-testimonial" element={<PrivateRoute path="/my-testimonial" title="My testimonial"><MyTestimonial /></PrivateRoute>} />
            <Route path="/admin/notifications" element={<PrivateRoute path="/admin/notifications" title="Notifications — Admin"><AdminNotifications /></PrivateRoute>} />
            <Route path="/admin/escalations" element={<PrivateRoute path="/admin/escalations" title="Escalations — Admin"><AdminEscalations /></PrivateRoute>} />
            <Route path="/admin/compassion" element={<PrivateRoute path="/admin/compassion" title="Compassion review — Admin"><AdminCompassion /></PrivateRoute>} />
            <Route path="/admin/intakes" element={<PrivateRoute path="/admin/intakes" title="Intakes — Admin"><AdminIntakes /></PrivateRoute>} />
            <Route path="/admin/built-index" element={<PrivateRoute path="/admin/built-index" title="Built Index — Admin"><AdminBuiltIndex /></PrivateRoute>} />

            <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
            <Route path="/qa/hero" element={<PrivateRoute path="/qa/hero" title="QA — Hero"><QAHero /></PrivateRoute>} />
            <Route path="/qa/hero/frame" element={<PrivateRoute path="/qa/hero/frame" title="QA — Hero frame"><QAHeroFrame /></PrivateRoute>} />
            <Route path="/qa/hero/perf" element={<PrivateRoute path="/qa/hero/perf" title="QA — Hero performance"><QAHeroPerf /></PrivateRoute>} />
            <Route path="/qa/about" element={<PrivateRoute path="/qa/about" title="QA — About"><QAAbout /></PrivateRoute>} />
            <Route path="/qa/about/frame" element={<PrivateRoute path="/qa/about/frame" title="QA — About frame"><QAAboutFrame /></PrivateRoute>} />


            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </ErrorBoundary>
        <ErrorBoundary label="FloatingMark" fallback={null}><FloatingMark /></ErrorBoundary>
        <ErrorBoundary label="LeadMagnetWidget" fallback={null}><LeadMagnetWidget /></ErrorBoundary>
        <ErrorBoundary label="StickyCTABar" fallback={null}><StickyCTABar /></ErrorBoundary>
        <ErrorBoundary label="CTAEventQA" fallback={null}><CTAEventQA /></ErrorBoundary>
        <ErrorBoundary label="HeroBudgetWarning" fallback={null}><HeroBudgetWarning /></ErrorBoundary>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;

